/** @jsx jsx */

/** @jsxFrag React.Fragment */
// HOC
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect } from 'react';

import { WES_ASK_COURSERA_HEADER_SLOT_ID } from '@coursera-web/wes-common';

import { isRightToLeft } from 'js/lib/language';
import { useLocation } from 'js/lib/useRouter';
import withUrlSessionStore from 'js/lib/withUrlSessionStore';

import { PageGridContainer, breakpoints } from '@coursera/cds-core';

import WesPageHeader from 'bundles/composable-page-header-enterprise/header-renderers/WesPageHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';
import type { ValidateBusinessEmailAction } from 'bundles/teams-v2/constants/businessEmail';
import type { ValueOf } from 'bundles/teams-v2/types/sharedTypes';
import { generateImpressionForMinm2Licenses } from 'bundles/teams-v2/utils/experimentUtils';
import { isPurchaseAccountPage } from 'bundles/teams-v2/utils/pathnameUtils';

// i18n
import _t from 'i18n!nls/teams-v2';

import 'css!./__styles__/TeamsMainApp';

import TeamsFooter from './TeamsFooter';

export type CheckoutSessionType = {
  businessEmail: string;
  fullName: string;
  alreadyC4TUserEmail?: string;
  role: string;
  organizationName: string;
  organizationType: string;
  organizationSizeCategory: string;
  promotionNotificationShown: string;
  promotionNotificationType?: string;
  _usid: string;
  emailVerificationType?: string;
  validatedEmail?: string;
  emailValidationStatus?: ValueOf<typeof ValidateBusinessEmailAction>;
  invoiceHostedUrl?: string;
  invoicePdfUrl?: string;
};

type Props = {};

const teamsV2AppContainerStyles = (isRTL: boolean) => css`
  margin: 0 auto;
  height: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: var(--cds-color-neutral-background-primary);

  /* TODO: Check if it's affecting performance, else upload to CDN */
  ${breakpoints.up('md')} {
    &::before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="116" viewBox="0 0 1440 616" fill="none"><path d="M1117.86 371.206L1658.81 864.261C1307.98 1249.17 779.069 1418.71 269.849 1309.47C-239.371 1200.23 -599.925 1191.77 -762.003 696.826L-420.501 697.5L-183.454 705.58C-102.414 953.05 56.8891 642.881 311.499 697.5C566.109 752.12 942.442 563.66 1117.86 371.206Z" fill="%233587FC"/><path d="M1278.52 288.032L1921.15 714.864C1617.45 1172.12 1093.67 1433.59 545.673 1401.5C-2.32014 1369.4 -301.744 1268.08 -549.997 778.496L-68.997 778.496C55.1305 1023.29 302.006 653.448 576.003 669.496C849.999 685.543 1126.66 516.661 1278.52 288.032Z" fill="%230056D2"/></svg>');
      background-position: bottom right;
      background-size: contain;
      background-repeat: no-repeat;
      transform: ${isRTL ? 'scaleX(-1)' : 'none'};
    }
  }
`;

const styles = {
  pageGridContainer: css`
    max-width: 1212px;
    padding-bottom: var(--cds-spacing-800) !important;
    margin-top: var(--cds-spacing-300);
    z-index: 1;

    > div:first-child {
      width: auto;
      margin: 0;
    }
  `,

  notificationContainer: css`
    margin-bottom: var(--cds-spacing-200);
  `,

  // Shared form styles for child components
  globalFormStyles: css`
    .teams-form-layout {
      ${breakpoints.up('md')} {
        display: grid;
        grid-template-columns: 9fr 6fr;
        grid-template-rows: auto 1fr;
        gap: var(--cds-spacing-150) var(--cds-spacing-400);
        align-items: start;
      }

      display: block;
    }
  `,
};

function NewCheckout(props: React.PropsWithChildren) {
  return props.children;
}

const renderWesAskCourseraHeaderSlot = () => (
  <div id={WES_ASK_COURSERA_HEADER_SLOT_ID} data-testid={WES_ASK_COURSERA_HEADER_SLOT_ID} />
);

export const TeamsMainApp = ({ children }: React.PropsWithChildren<Props>) => {
  const { pathname } = useLocation();
  const path = (pathname && pathname.split('/').pop()) || '';
  const wrapperClass = 'rc-TeamsMainApp theme-light';
  const isRTL = isRightToLeft(_t.getLocale());

  const headerEndContent = isPurchaseAccountPage(pathname) ? renderWesAskCourseraHeaderSlot : undefined;

  // Generate minm2Licenses impression once at the start of the user journey
  useEffect(() => {
    generateImpressionForMinm2Licenses();
  }, []);

  if (path === 'confirmation' || path === 'free-trial-confirmation') {
    return (
      <div className={wrapperClass} css={[teamsV2AppContainerStyles(isRTL), styles.globalFormStyles]}>
        <UniversalPageFeatures />
        <WesPageHeader logoType="c4t" hideMetaNav renderHeaderEndContent={headerEndContent} />
        {children}
      </div>
    );
  }

  return (
    <div css={[teamsV2AppContainerStyles(isRTL), styles.globalFormStyles]}>
      <CourseraMetatags metaNameAndProperties={{ disableCrawlerIndexing: true }} />
      <UniversalPageFeatures />
      <WesPageHeader logoType="c4t" hideMetaNav renderHeaderEndContent={headerEndContent} />
      <PageGridContainer css={styles.pageGridContainer}>
        <NewCheckout>{children}</NewCheckout>
      </PageGridContainer>
      <TeamsFooter />
    </div>
  );
};

export default withUrlSessionStore<$TSFixMe, React.PropsWithChildren<{}>>()(TeamsMainApp);
